import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, CheckCircle, Star, Users, Navigation, ArrowRight, Calendar, Info } from 'lucide-react';

import ModernSelect from '../components/ModernSelect';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
    const { t } = useLanguage();

    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <div className="pt-16 lg:pt-20">
            {/* Enhanced Header */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-6 py-3 rounded-full text-sm font-medium mb-8"
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span>{t('contact.getInTouch')}</span>
                        </motion.div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-white leading-tight">
                            <span className="text-gradient">{t('contact.title')}</span> {t('contact.contactOurTeam')}
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-12">
                            {t('contact.readyToExperience')}
                        </p>

                        {/* Quick Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex justify-center items-center space-x-8 flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Phone className="w-5 h-5 text-primary-400" />
                                <span className="text-sm text-gray-300 font-medium">+243 971 566 525</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Clock className="w-5 h-5 text-accent-500" />
                                <span className="text-sm text-gray-300 font-medium">Tue-Sun: 11AM-11PM</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <MapPin className="w-5 h-5 text-secondary-500" />
                                <span className="text-sm text-gray-300 font-medium">Goma, Congo-Kinshasa</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Contact Information */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                        {/* Enhanced Contact Details */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            {/* Header */}
                            <div>
                                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                                    <Info className="w-4 h-4" />
                                    <span>{t('contact.contactDetails')}</span>
                                </div>
                                <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4 text-gray-900 dark:text-white">
                                    {t('contact.howToReachUs')}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {t('contact.helpWithReservations')}
                                </p>
                            </div>

                            {/* Contact Cards */}
                            <div className="space-y-6">
                                {[
                                    {
                                        icon: MapPin,
                                        title: t('contact.visitRestaurant'),
                                        content: (
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">av. Lyn Lusi 058</p>
                                                <p>Goma</p>
                                                <p>Congo-Kinshasa</p>
                                            </div>
                                        ),
                                        action: t('contact.getDirections'),
                                        color: "from-primary-500 to-primary-600"
                                    },
                                    {
                                        icon: Phone,
                                        title: t('contact.callUsDirectly'),
                                        content: (
                                            <div>
                                                <a
                                                    href="tel:+243971566525"
                                                    className="font-medium text-primary-600 hover:text-primary-700 transition-colors text-lg"
                                                >
                                                    +243 971 566 525
                                                </a>
                                                <p className="text-sm mt-1">{t('contact.availableDuringBusiness')}</p>
                                            </div>
                                        ),
                                        action: t('contact.callNow'),
                                        color: "from-accent-500 to-accent-600"
                                    },
                                    {
                                        icon: Mail,
                                        title: t('contact.sendUsEmail'),
                                        content: (
                                            <div>
                                                <a
                                                    href="mailto:contact@ilrasentino.com"
                                                    className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
                                                >
                                                    contact@ilrasentino.com
                                                </a>
                                                <p className="text-sm mt-1">{t('contact.respondWithin24')}</p>
                                            </div>
                                        ),
                                        action: t('contact.sendEmail'),
                                        color: "from-secondary-500 to-secondary-600"
                                    },
                                    {
                                        icon: Clock,
                                        title: t('contact.openingHours'),
                                        content: (
                                            <div className="space-y-1">
                                                <div className="flex justify-between">
                                                    <span className="font-medium">{t('contact.monday')}:</span>
                                                    <span className="text-red-600 font-medium">{t('contact.closed')}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="font-medium">{t('contact.tuesdayToSunday')}:</span>
                                                    <span className="text-green-600 font-medium">11:00 AM - 11:00 PM</span>
                                                </div>
                                            </div>
                                        ),
                                        action: t('contact.bookTable'),
                                        color: "from-green-500 to-green-600"
                                    }
                                ].map((contact, index) => (
                                    <motion.div
                                        key={contact.title}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="group"
                                    >
                                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:-translate-y-1">
                                            <div className="flex items-start space-x-4">
                                                <div className={`w-16 h-16 bg-gradient-to-br ${contact.color} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                                    <contact.icon className="w-8 h-8 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                                                        {contact.title}
                                                    </h3>
                                                    <div className="text-gray-600 dark:text-gray-300 mb-3">
                                                        {contact.content}
                                                    </div>
                                                    <button className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center space-x-1 transition-colors">
                                                        <span>{contact.action}</span>
                                                        <ArrowRight className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Enhanced Map */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="sticky top-8">
                                <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center space-x-3 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                                            <Navigation className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('contact.findUs')}</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">av. Lyn Lusi 058, Goma</p>
                                        </div>
                                    </div>

                                    <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl h-80 overflow-hidden mb-4">
                                        <iframe
                                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5!2d15.3!3d-4.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNMKwMTgnMDAuMCJTIDE1wrAxOCcwMC4wIkU!5e0!3m2!1sen!2sus!4v1234567890"
                                            width="100%"
                                            height="100%"
                                            style={{ border: 0 }}
                                            allowFullScreen
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            title="restaurant Location"
                                        />
                                    </div>

                                    {/* Location Stats */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                            <div className="text-lg font-bold text-primary-600">15 {t('contact.minutes')}</div>
                                            <div className="text-xs text-gray-600 dark:text-gray-400">{t('contact.fromCityCenter')}</div>
                                        </div>
                                        <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                                            <div className="text-lg font-bold text-accent-600">{t('contact.free')}</div>
                                            <div className="text-xs text-gray-600 dark:text-gray-400">{t('contact.parkingAvailable')}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Enhanced Contact Form */}
            <section className="relative section-padding bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-3">
                    <div className="w-full h-full" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E44893' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto"
                    >
                        {/* Header */}
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                                <Send className="w-4 h-4" />
                                <span>{t('contact.sendMessage')}</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-gray-900 dark:text-white">
                                {t('contact.getInTouchWithUs')}
                            </h2>
                            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                                {t('contact.haveQuestion')}
                            </p>
                        </div>

                        {/* Enhanced Form Container */}
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-100 dark:border-gray-700">
                            <form className="space-y-8">
                                {/* Personal Information */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <Users className="w-5 h-5 text-primary-600" />
                                        <span>{t('contact.personalInformation')}</span>
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.firstName')} *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('contact.enterFirstName')}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.lastName')} *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('contact.enterLastName')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Information */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <MessageCircle className="w-5 h-5 text-accent-600" />
                                        <span>{t('contact.contactInformation')}</span>
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.emailAddress')} *
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('contact.emailPlaceholder')}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.phoneNumber')}
                                            </label>
                                            <input
                                                type="tel"
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                                placeholder={t('contact.phonePlaceholder')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Message Details */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white flex items-center space-x-2">
                                        <Mail className="w-5 h-5 text-secondary-600" />
                                        <span>{t('contact.messageDetails')}</span>
                                    </h3>
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.subject')} *
                                            </label>
                                            <ModernSelect
                                                options={[
                                                    { value: 'reservation', label: t('contact.reservationInquiry') },
                                                    { value: 'general', label: t('contact.generalInquiry') },
                                                    { value: 'feedback', label: t('contact.feedbackReviews') },
                                                    { value: 'private-event', label: t('contact.privateEventBooking') },
                                                    { value: 'catering', label: t('contact.cateringServices') },
                                                    { value: 'dietary', label: t('contact.dietaryRequirements') },
                                                    { value: 'other', label: t('contact.other') }
                                                ]}
                                                placeholder={t('contact.selectPurpose')}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                                {t('contact.yourMessage')} *
                                            </label>
                                            <textarea
                                                required
                                                rows={6}
                                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-none"
                                                placeholder={t('contact.messagePlaceholder')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Section */}
                                <div className="border-t border-gray-200 dark:border-gray-600 pt-8">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            <span>{t('contact.respondWithin24Hours')}</span>
                                        </div>
                                        <motion.button
                                            type="submit"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
                                        >
                                            <Send className="w-5 h-5" />
                                            <span>{t('contact.sendMessage')}</span>
                                        </motion.button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced CTA Section */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        {/* Badge */}
                        <div className="inline-flex items-center space-x-2 bg-white/10 text-white px-6 py-3 rounded-full text-sm font-medium mb-8">
                            <Calendar className="w-5 h-5" />
                            <span>{t('contact.readyToDine')}</span>
                        </div>

                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 leading-tight">
                            {t('contact.experienceAuthentic')}
                        </h2>
                        <p className="text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
                            {t('contact.readyToEmbark')}
                        </p>

                        {/* Enhanced Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center max-w-lg mx-auto mb-12">
                            <motion.a
                                href="tel:+243971566525"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 bg-white text-gray-900 hover:bg-gray-100 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                <Phone className="w-5 h-5 text-primary-600 group-hover:animate-pulse" />
                                <span>{t('contact.callNumber')}</span>
                            </motion.a>
                            <motion.a
                                href="/reservation"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="group w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold py-4 px-8 rounded-2xl transition-all duration-300 inline-flex items-center justify-center space-x-3"
                            >
                                <Calendar className="w-5 h-5" />
                                <span>{t('contact.bookOnline')}</span>
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                            </motion.a>
                        </div>

                        {/* Quick Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                                <Clock className="w-8 h-8 text-primary-400 mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('contact.quickResponse')}</h4>
                                <p className="text-white/80 text-sm">{t('contact.confirmWithin2Hours')}</p>
                            </div>
                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                                <Star className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('contact.premiumExperience')}</h4>
                                <p className="text-white/80 text-sm">{t('contact.diningEntertainment')}</p>
                            </div>
                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                                <MapPin className="w-8 h-8 text-accent-500 mx-auto mb-3" />
                                <h4 className="text-white font-semibold mb-2">{t('contact.perfectLocation')}</h4>
                                <p className="text-white/80 text-sm">{t('contact.easyToFind')}</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Contact;
